import Component from '@glimmer/component';
import ENV from 'tio-employee/config/environment';
import pageTitle from 'ember-page-title/helpers/page-title';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import { VStack } from 'tio-ui/components/layout';

interface UnsubscribeSignature {
  Args: {
    model: {
      token: string;
    };
  };
}

// Public unsubscribe explainer page (concept A1). Explains that the email is an
// employer-sponsored benefit notification (not marketing), warns about what the
// recipient may miss, and surfaces the actual unsubscribe action. The action
// POSTs to the public tio-api `/u/:token` endpoint, which logs the click.
export default class UnsubscribePage extends Component<UnsubscribeSignature> {
  @tracked unsubscribed = false;

  get token() {
    return this.args.model.token;
  }

  unsubscribe = dropTask(async () => {
    // Confirm immediately — the logging POST is best-effort and must never
    // block (or, on a hung request, prevent) the confirmation state.
    this.unsubscribed = true;

    try {
      // `source=explainer_button` lets tio-api log this as an actual on-page
      // unsubscribe click (vs. the page-view ping or a mailbox one-click).
      // No body/custom headers: a CORS "simple request" (no preflight) so the
      // best-effort logging call reaches tio-api even cross-origin. `keepalive`
      // lets it complete even if the user navigates away right after confirming.
      await fetch(`${ENV.apiHost}/u/${encodeURIComponent(this.token)}?source=explainer_button`, {
        method: 'POST',
        keepalive: true,
      });
    } catch {
      // Best-effort logging: the confirmation is already shown.
    }
  });

  <template>
    {{pageTitle (t "unsubscribe.page_title")}}

    <div class="tio-col container mx-auto max-w-2xl p-6">
      <Card @padding="lg">
        {{#if this.unsubscribed}}
          <VStack>
            <h1>{{t "unsubscribe.confirmed_heading"}}</h1>
            <p class="tio-fg-secondary">{{t "unsubscribe.confirmed_body"}}</p>
          </VStack>
        {{else}}
          <VStack>
            <h1>{{t "unsubscribe.heading"}}</h1>
            <p class="tio-fg-secondary">{{t "unsubscribe.benefit_intro"}}</p>
            <p class="tio-fg-secondary">{{t "unsubscribe.not_marketing"}}</p>
            <p class="tio-fg-secondary">{{t "unsubscribe.warning"}}</p>
            <p class="tio-fg-secondary">{{t "unsubscribe.contact_hr"}}</p>

            <div class="tio-cluster">
              <Button
                @intent="primary"
                @isRunning={{this.unsubscribe.isRunning}}
                {{on "click" this.unsubscribe.perform}}
              >
                {{t "unsubscribe.unsubscribe_button"}}
              </Button>
            </div>
          </VStack>
        {{/if}}
      </Card>
    </div>
  </template>
}
